import { App as AntdApp, ConfigProvider } from 'antd'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { QueryClient, QueryClientProvider } from 'react-query'
import { RouterProvider } from 'react-router'
import { ToastContainer } from 'react-toastify'
import { ConfirmProvider } from 'sutra/components/confirm/ConfirmProvider'
import { accents } from 'sutra/tokens/colors'
import { colorToHex } from 'sutra/utils/color'
import { TOAST_AUTO_CLOSE_MS } from 'sutra/utils/toast-constants'
import { renderToastIcon, toastTransition } from 'sutra/utils/ToastConfig'

import { ApiClientProvider } from 'clients/yogi-api'
import { YogiAxiosClient } from 'clients/yogi-api/client'
import { GlobalBootstrapOverlay } from 'components'
import { BulkAddJobPollerContainer } from 'features/settings/features/access/components/bulk-add-job-poller'
import { router } from 'routes'
import { initChameleon } from 'trackers/chameleon'
import { isTrackersEnabled } from 'trackers/helper'
import { loadTrackers } from 'trackers/index'
import 'clients/amplify'

import 'normalize.css'
import 'antd/dist/reset.css'
import 'rc-color-picker/assets/index.css'
import 'react-toastify/dist/ReactToastify.css'
import 'react-grid-layout/css/styles.css'
import 'sutra/fonts'

import 'sutra/sutra.css'
import 'assets/styles/base.css'
import 'assets/styles/toast.css'
import 'assets/styles/variables.css'
import 'chartjs-plugin-datalabels/dist/chartjs-plugin-datalabels.min'
import 'chartjs-chart-treemap'

const queryClient = new QueryClient()
const yogiClient = new YogiAxiosClient()

if (isTrackersEnabled()) {
  void loadTrackers()
}
// Chameleon can run without other trackers (no-ops when VITE_CHAMELEON_APP_ID is unset)
initChameleon()

export const App = (): JSX.Element => {
  return (
    <ApiClientProvider client={yogiClient}>
      <ConfigProvider
        theme={{
          token: {
            fontFamily: 'Poppins, sans-serif',
            lineHeight: 1.5715,
            // antd's @ant-design/colors palette generator can't parse an
            // oklch() string (it silently returns black), so convert the
            // Sutra token to hex rather than passing it straight through.
            colorPrimary: colorToHex(accents.reflectionBlue[500]),
          },
        }}
      >
        <AntdApp>
          <ConfirmProvider>
            <QueryClientProvider client={queryClient}>
              <DndProvider backend={HTML5Backend}>
                <GlobalBootstrapOverlay />
                {/*
                Resumes in-flight bulk-add jobs across F5 / route changes
                and surfaces the result toast. Renders nothing; mounted
                here at App-shell level so the operator sees the toast
                regardless of which page they navigated to.
              */}
                <BulkAddJobPollerContainer />
                <RouterProvider router={router} />
                <ToastContainer
                  hideProgressBar={true}
                  position="bottom-right"
                  autoClose={TOAST_AUTO_CLOSE_MS}
                  transition={toastTransition}
                  icon={renderToastIcon}
                />
              </DndProvider>
            </QueryClientProvider>
          </ConfirmProvider>
        </AntdApp>
      </ConfigProvider>
    </ApiClientProvider>
  )
}

export default App
